import { Alerta, Cartao, Etiqueta, Tabela } from '../comum/ui.js';

/**
 * TRANSMISSÃO — fila de eventos, estados e rejeições traduzidas.
 *
 * A coluna que importa é a última: a ocorrência do governo TRADUZIDA. "Erro
 * 1234 no campo perApur" não diz ao DP o que fazer; "o 13º é enviado com
 * período anual, corrija para 2026" diz.
 */

const FLUXO = [
  'RASCUNHO',
  'VALIDADO_LOCAL',
  'ASSINADO',
  'NA_FILA',
  'ENVIADO',
  'PROCESSANDO_GOV',
  'PROCESSADO',
] as const;

export function Transmissao({ competencia }: { competencia: string }) {
  return (
    <div className="tela">
      <h1>Transmissão — {competencia}</h1>

      <Alerta severidade="INFO" titulo="Ambiente de produção restrita">
        Todos os eventos vão para o ambiente restrito. A troca para produção real depende de
        credencial e de certificado válido (PE-05 e PE-06) e é uma decisão por empresa, no cadastro.
      </Alerta>

      <Cartao titulo="Como um evento caminha">
        <ol className="fluxo">
          {FLUXO.map((estado, i) => (
            <li key={estado} className="fluxo__passo">
              <span className="fluxo__numero">{i + 1}</span>
              <div>
                <strong>{estado.replace(/_/g, ' ')}</strong>
                <p className="sutil">{DESCRICAO_ESTADO[estado]}</p>
              </div>
            </li>
          ))}
        </ol>

        <Alerta severidade="ATENCAO" titulo="Estados terminais">
          Um evento <strong>PROCESSADO</strong> não volta para rascunho. Corrigir significa
          RETIFICAR (novo evento com <code>indRetif=2</code> apontando o recibo original) ou EXCLUIR
          (S-3000) — e as duas transições exigem motivo registrado na trilha.
        </Alerta>
      </Cartao>

      <Cartao titulo="Ordem de dependência">
        <Tabela cabecalhos={['Grupo', 'Ordem dentro do grupo', 'Depende de']}>
          <tr>
            <td>
              <Etiqueta texto="TABELAS" tom="neutro" />
            </td>
            <td>S-1000 → S-1005 → S-1010 → S-1020</td>
            <td>—</td>
          </tr>
          <tr>
            <td>
              <Etiqueta texto="NÃO PERIÓDICOS" tom="neutro" />
            </td>
            <td>S-2190 → S-2200 → S-2205/2206 → S-2230 → S-2299</td>
            <td>Tabelas processadas</td>
          </tr>
          <tr>
            <td>
              <Etiqueta texto="PERIÓDICOS" tom="neutro" />
            </td>
            <td>S-1298 → S-1200 → S-1210 → S-1299</td>
            <td>
              S-2200 do trabalhador PROCESSADO.
              <br />
              O S-1299 exige <strong>todos</strong> os periódicos processados.
            </td>
          </tr>
          <tr>
            <td>
              <Etiqueta texto="EXCLUSÃO" tom="atencao" />
            </td>
            <td>S-3000</td>
            <td>Recibo do evento a excluir</td>
          </tr>
        </Tabela>

        <p className="sutil">
          A verificação roda ANTES do envio: se faltar dependência, o lote nem sai — em vez de
          descobrir pela rejeição em cascata do governo.
        </p>
      </Cartao>

      <Cartao titulo="Conferência de fechamento">
        <p>
          Antes do S-1299, os totalizadores devolvidos pelo governo (S-5001 e S-5011) são comparados
          com o <code>ResultadoFolha</code>, campo a campo. Divergência acima de R$ 0,01{' '}
          <strong>bloqueia o fechamento</strong>.
        </p>
        <p className="sutil">
          É aqui que se pega a rubrica cadastrada com incidência errada: o governo apura uma base,
          nós apuramos outra, e a diferença aparece agora — não meses depois, na malha do DCTFWeb.
        </p>
      </Cartao>
    </div>
  );
}

const DESCRICAO_ESTADO: Record<(typeof FLUXO)[number], string> = {
  RASCUNHO: 'Evento montado a partir do ResultadoFolha, ainda sem validação.',
  VALIDADO_LOCAL: 'Passou por Zod (estrutura), XSD (leiaute) e pelas regras REGRA_*.',
  ASSINADO: 'Assinado com XMLDSig usando o certificado resolvido pelo empresaId.',
  NA_FILA: 'Aguardando o worker de transmissão.',
  ENVIADO: 'Lote entregue ao webservice; protocolo recebido.',
  PROCESSANDO_GOV: 'Governo processando o lote.',
  PROCESSADO: 'Aceito, com nrRecibo — a prova de que o evento existe lá.',
};
